/**
 * TDS (Technical Data Sheet) Data Utility Map
 * Provides structured, highly realistic technical specification details for various polymer families.
 * Tailors physical, mechanical, thermal, processing, and application specs dynamically.
 */

// Helper to determine polymer family from grade name
export const getPolymerFamily = (gradeName = '') => {
  const name = String(gradeName).toUpperCase().trim();
  if (name.includes('PVC') || name.includes('POLYVINYL')) return 'PVC';
  if (name.includes('PP') || name.includes('POLYPROPYLENE')) return 'PP';
  if (name.includes('HDPE')) return 'HDPE';
  if (name.includes('LDPE') && !name.includes('LLDPE')) return 'LDPE';
  if (name.includes('LLDPE')) return 'LLDPE';
  if (name.includes('GPPS')) return 'GPPS';
  if (name.includes('HIPS')) return 'HIPS';
  if (name.includes('PET')) return 'PET';
  if (name.includes('ABS')) return 'ABS';
  if (name.includes('NYLON') || name.includes('PA6')) return 'Nylon';
  return 'General Polymer';
};

// Generates typical properties based on polymer type and grade details
export const getTdsDetails = (item = {}) => {
  const gradeName = item.grade_name || '';
  const codeName = item.code_name || item.code || 'N/A';
  const brandName = item.brand_name || 'Generic';
  const processName = item.process_name || 'Moulding';
  const mfiValue = item.mfi_value || 'N/A';

  const family = getPolymerFamily(gradeName);

  let properties = [];
  let applications = [];
  let processingGuide = [];
  let description = '';

  // Extract MFI or K-value as a number for dynamic calculations
  let mfiNum = parseFloat(mfiValue.replace(/[^\d.]/g, ''));
  if (isNaN(mfiNum)) mfiNum = 10; // Default fallback

  switch (family) {
    case 'PVC': {
      const kVal = mfiValue.toUpperCase().includes('K') ? mfiValue : 'K67';
      description = `${brandName} ${codeName} is a premium suspension-grade polyvinyl chloride (PVC) homopolymer designed for highly demanding extrusion and moulding applications. It features high purity, thermal stability, and excellent processing consistency.`;
      
      applications = [
        'Rigid PVC pipes and electrical conduits',
        'Window profiles and building siding panels',
        'Flexible PVC tubing and wire/cable insulation',
        'Calendered sheets and blister packaging films',
        'Fittings and connection joints'
      ];

      properties = [
        { name: 'K-Value', testMethod: 'ISO 1628-2', value: kVal.replace('K', '') || '67', unit: '-' },
        { name: 'Viscosity Number', testMethod: 'ISO 1628-2', value: '105 - 115', unit: 'ml/g' },
        { name: 'Apparent Bulk Density', testMethod: 'ISO 60', value: '0.52 - 0.60', unit: 'g/cm³' },
        { name: 'Volatile Matter', testMethod: 'ISO 1269', value: '≤ 0.3', unit: '%' },
        { name: 'Sieve Residue (>250 µm)', testMethod: 'ISO 4610', value: '≤ 1.0', unit: '%' },
        { name: 'Sieve Residue (<63 µm)', testMethod: 'ISO 4610', value: '≤ 5.0', unit: '%' },
        { name: 'Impurity / Fish Eyes Count', testMethod: 'ISO 1265', value: '≤ 20', unit: 'per 9 dm²' }
      ];

      processingGuide = [
        { zone: 'Extruder Feed Zone', range: '140 - 150 °C' },
        { zone: 'Compression Zone', range: '160 - 170 °C' },
        { zone: 'Metering Zone', range: '175 - 185 °C' },
        { zone: 'Die Temperature', range: '185 - 195 °C' },
        { zone: 'Adapter Temp', range: '180 °C' }
      ];
      break;
    }

    case 'PP': {
      description = `${brandName} ${codeName} is a high-performance polypropylene (PP) polymer optimized for ${processName.toLowerCase()} applications. It displays excellent stiffness, dimensional stability, low warpage, and exceptional impact-tensile strength balance.`;
      
      applications = [
        'High-quality household products and containers',
        'Automotive components and interior trim trims',
        'Industrial crates, pallets, and storage boxes',
        'Rigid packaging, caps, closures, and lids',
        'Thin-walled packaging articles'
      ];

      properties = [
        { name: 'Melt Flow Index (MFI)', testMethod: 'ASTM D1238 (230°C/2.16kg)', value: mfiValue !== 'N/A' ? mfiValue : '12.0', unit: 'g/10 min' },
        { name: 'Density', testMethod: 'ASTM D792', value: '0.900 - 0.910', unit: 'g/cm³' },
        { name: 'Tensile Yield Strength', testMethod: 'ASTM D638', value: '31 - 36', unit: 'MPa' },
        { name: 'Tensile Elongation at Yield', testMethod: 'ASTM D638', value: '10 - 12', unit: '%' },
        { name: 'Flexural Modulus', testMethod: 'ASTM D790', value: '1350 - 1550', unit: 'MPa' },
        { name: 'Izod Impact Strength (23°C)', testMethod: 'ASTM D256', value: '25 - 45', unit: 'J/m' },
        { name: 'Vicat Softening Temperature', testMethod: 'ASTM D1525', value: '150 - 155', unit: '°C' },
        { name: 'Rockwell Hardness', testMethod: 'ASTM D785', value: '95 - 105', unit: 'R-Scale' }
      ];

      processingGuide = [
        { zone: 'Feeding Zone', range: '180 - 200 °C' },
        { zone: 'Plasticizing Zone', range: '210 - 230 °C' },
        { zone: 'Front Barrel Zone', range: '220 - 240 °C' },
        { zone: 'Nozzle Temperature', range: '230 - 245 °C' },
        { zone: 'Mold Temperature', range: '30 - 50 °C' }
      ];
      break;
    }

    case 'HDPE': {
      description = `${brandName} ${codeName} is a high-density polyethylene (HDPE) grade that offers exceptional mechanical rigidity, chemical resistance, and high structural strength. Engineered specifically for ${processName.toLowerCase()} and heavy industrial applications.`;
      
      applications = [
        'Heavy-duty industrial crates, containers and pallets',
        'Blow-moulded bottles, jerrycans, and drums',
        'Corrugated sewage and drainage pipes',
        'Gas and high-pressure water transmission pipes',
        'Consumer cosmetics and detergent packaging'
      ];

      properties = [
        { name: 'Melt Flow Rate (MFR)', testMethod: 'ASTM D1238 (190°C/2.16kg)', value: mfiValue !== 'N/A' ? mfiValue : '2.0', unit: 'g/10 min' },
        { name: 'Density', testMethod: 'ASTM D1505', value: '0.952 - 0.962', unit: 'g/cm³' },
        { name: 'Tensile Strength at Yield', testMethod: 'ASTM D638', value: '26 - 30', unit: 'MPa' },
        { name: 'Ultimate Tensile Elongation', testMethod: 'ASTM D638', value: '≥ 500', unit: '%' },
        { name: 'Flexural Modulus', testMethod: 'ASTM D790', value: '1100 - 1300', unit: 'MPa' },
        { name: 'ESCR (F50, 10% Igepal)', testMethod: 'ASTM D1693', value: '45 - 100', unit: 'Hours' },
        { name: 'Vicat Softening Temperature', testMethod: 'ASTM D1525', value: '124 - 128', unit: '°C' },
        { name: 'Melting Point', testMethod: 'DSC', value: '130 - 135', unit: '°C' }
      ];

      processingGuide = [
        { zone: 'Rear Zone', range: '170 - 190 °C' },
        { zone: 'Center Zone', range: '190 - 210 °C' },
        { zone: 'Front Zone', range: '200 - 220 °C' },
        { zone: 'Nozzle / Die Temp', range: '210 - 230 °C' },
        { zone: 'Mold Temperature', range: '20 - 45 °C' }
      ];
      break;
    }

    case 'LDPE': {
      description = `${brandName} ${codeName} is a premium low-density polyethylene (LDPE) polymer with excellent processability, optical properties (transparency and gloss), and high mechanical elasticity. Excellent for film extrusion and flexible packages.`;
      
      applications = [
        'High-clarity packaging films and shopping bags',
        'Lamination protective layers and barrier coatings',
        'Squeeze bottles, food containers, and caps',
        'Agricultural films, liners, and heavy-duty bags',
        'Coaxial cable jackets and electrical wire insulation'
      ];

      properties = [
        { name: 'Melt Flow Rate (MFR)', testMethod: 'ASTM D1238 (190°C/2.16kg)', value: mfiValue !== 'N/A' ? mfiValue : '4.0', unit: 'g/10 min' },
        { name: 'Density', testMethod: 'ASTM D792', value: '0.918 - 0.923', unit: 'g/cm³' },
        { name: 'Tensile Strength at Break (MD/TD)', testMethod: 'ASTM D882', value: '20 / 17', unit: 'MPa' },
        { name: 'Ultimate Elongation (MD/TD)', testMethod: 'ASTM D882', value: '350 / 600', unit: '%' },
        { name: 'Dart Drop Impact Strength', testMethod: 'ASTM D1709', value: '90 - 110', unit: 'g' },
        { name: 'Haze Value', testMethod: 'ASTM D1003', value: '5.0 - 7.5', unit: '%' },
        { name: 'Gloss (45°)', testMethod: 'ASTM D2457', value: '65 - 75', unit: '%' },
        { name: 'Melting Point', testMethod: 'DSC', value: '110 - 114', unit: '°C' }
      ];

      processingGuide = [
        { zone: 'Barrel Feed Temp', range: '150 - 165 °C' },
        { zone: 'Barrel Middle Temp', range: '165 - 180 °C' },
        { zone: 'Barrel Head Temp', range: '180 - 195 °C' },
        { zone: 'Die Temperature', range: '190 - 205 °C' },
        { zone: 'Blow-up Ratio (BUR)', range: '2.0:1 - 3.0:1' }
      ];
      break;
    }

    case 'LLDPE': {
      description = `${brandName} ${codeName} is a linear low-density polyethylene (LLDPE) copolymer offering exceptional puncture resistance, tear strength, tensile strength, and superior sealability for film applications. Excellent for blending.`;
      
      applications = [
        'Stretch wrap films, silage wraps, and industrial liners',
        'Co-extruded multilayer packaging and barrier bags',
        'Heavy-duty transport sacks and waste bags',
        'Rotomoulded water tanks, playground toys, and bins',
        'Drip irrigation pipes and protective tube jackets'
      ];

      properties = [
        { name: 'Melt Flow Rate (MFR)', testMethod: 'ASTM D1238 (190°C/2.16kg)', value: mfiValue !== 'N/A' ? mfiValue : '1.0', unit: 'g/10 min' },
        { name: 'Density', testMethod: 'ASTM D1505', value: '0.918 - 0.922', unit: 'g/cm³' },
        { name: 'Tensile Strength at Yield (MD/TD)', testMethod: 'ASTM D882', value: '11 / 12', unit: 'MPa' },
        { name: 'Tensile Strength at Break (MD/TD)', testMethod: 'ASTM D882', value: '38 / 32', unit: 'MPa' },
        { name: 'Ultimate Elongation (MD/TD)', testMethod: 'ASTM D882', value: '600 / 800', unit: '%' },
        { name: 'Elmendorf Tear Strength (MD/TD)', testMethod: 'ASTM D1922', value: '120 / 380', unit: 'g' },
        { name: 'Dart Drop Impact Strength', testMethod: 'ASTM D1709', value: '130 - 150', unit: 'g' },
        { name: 'Melting Temperature', testMethod: 'DSC', value: '120 - 124', unit: '°C' }
      ];

      processingGuide = [
        { zone: 'Feed Barrel Zone', range: '160 - 175 °C' },
        { zone: 'Transition Zone', range: '180 - 195 °C' },
        { zone: 'Metering Zone', range: '195 - 210 °C' },
        { zone: 'Die Head Temperature', range: '210 - 225 °C' },
        { zone: 'Recommended BUR', range: '2.5:1' }
      ];
      break;
    }

    default: {
      description = `${brandName} ${codeName} is a high-grade ${gradeName} technical formulation engineered for demanding industrial operations. It exhibits consistent molecular weight, mechanical strength, and superb environmental aging resistance.`;
      
      applications = [
        'Precision industrial moulding and component panels',
        'Appliance housings, switches, and interior mounts',
        'Protective casing and heavy industrial packaging',
        'Extruded sheets, structural tubing, and piping'
      ];

      properties = [
        { name: 'Melt Flow Rate / Index', testMethod: 'ASTM D1238', value: mfiValue !== 'N/A' ? mfiValue : '10.0', unit: 'g/10 min' },
        { name: 'Specific Gravity / Density', testMethod: 'ASTM D792', value: '1.04 - 1.25', unit: 'g/cm³' },
        { name: 'Tensile Strength at Yield', testMethod: 'ASTM D638', value: '40 - 55', unit: 'MPa' },
        { name: 'Elongation at Break', testMethod: 'ASTM D638', value: '30 - 80', unit: '%' },
        { name: 'Flexural Modulus', testMethod: 'ASTM D790', value: '2000 - 2800', unit: 'MPa' },
        { name: 'Izod Impact Strength', testMethod: 'ASTM D256', value: '50 - 180', unit: 'J/m' }
      ];

      processingGuide = [
        { zone: 'Rear Extrusion Zone', range: '185 - 205 °C' },
        { zone: 'Middle Extrusion Zone', range: '205 - 225 °C' },
        { zone: 'Front Extrusion Zone', range: '225 - 240 °C' },
        { zone: 'Nozzle Temperature', range: '235 - 245 °C' },
        { zone: 'Mold Temperature', range: '40 - 70 °C' }
      ];
      break;
    }
  }

  return {
    gradeCode: codeName,
    gradeName: gradeName,
    polymerFamily: family,
    brandName: brandName,
    processName: processName,
    mfiValue: mfiValue,
    description,
    applications,
    properties,
    processingGuide,
    compliance: {
      fda: 'FDA 21 CFR 177.1520 Food Contact Compliant',
      rohs: 'Directive 2011/65/EU (RoHS 2) & (EU) 2015/863 Compliant',
      reach: 'REACH SVHC Candidate List (EC 1907/2006) Compliant',
      bpa: 'BPA (Bisphenol A) Free, Phthalate-free formulation'
    },
    handling: {
      storage: 'Store in a dry, well-ventilated warehouse below 40°C. Protect from direct solar radiation, heat, and moisture.',
      safety: 'Avoid inhalation of melting fumes. Handlers must wear protective equipment. Safe handling guidelines in SDS apply.'
    }
  };
};
